import { useEffect, useState } from 'react';

import { getInjector } from '@/services/angularjs-bridge';

enum AUTH_OBJECT_TYPES {
    ALL = '*',
    ANY = '?',
    OBJECT_TYPE_COMMUNICATION_CHANNELS = 'communication_channels',
    OBJECT_TYPE_CONTACT = 'contacts',
    OBJECT_TYPE_DATAMODEL = 'datamodel',
    OBJECT_TYPE_ITEMS = 'items',
    OBJECT_TYPE_TASK_LISTS = 'taskLists',
    OBJECT_TYPE_UI = 'ui',
    OBJECT_TYPE_ENHANCED_CONTENTS = 'enhancedContents',
    OBJECT_TYPE_BULK_SUBSCRIPTIONS = 'bulkSubscriptions',
}

type AuthService = {
    hasAnyPermission: (objectType: AUTH_OBJECT_TYPES, actions: string | string[], context?: Record<string, string>) => boolean;
    isDenied: (objectType: AUTH_OBJECT_TYPES, action: string, context?: Record<string, string>) => boolean;
    hasItemPermission : (actions: string, item : any) => boolean;
}

/**
 * Checks if we're running in Storybook environment
 */
const isStorybook = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.includes('/storybook') ||
        window.location.pathname.includes('/iframe.html') ||
        window.parent !== window; // Storybook runs in an iframe
};

/**
 * Creates a mock auth service for Storybook that always grants permissions
 */
const createMockAuthService = (): AuthService => {
    return {
        hasAnyPermission: () => true,
        isDenied: () => false,
        hasItemPermission: () => true,
    };
};

const useAuth = () => {

    const [authService, setAuthService] = useState<AuthService | undefined>();

    useEffect(() => {
        // In Storybook, return a mock auth service that always returns true
        if (isStorybook()) {
            setAuthService(createMockAuthService());
            return;
        }

        // Otherwise, use the real AngularJS injector
        try {
            const angularJSInjector = getInjector();
            const authService = angularJSInjector.get('Auth') as AuthService;
            setAuthService(authService);
        } catch (error) {
            // Fallback to mock if injector is not available
            setAuthService(createMockAuthService());
        }
    }, []);

    return { auth: authService };
};

export { AUTH_OBJECT_TYPES, useAuth };
